import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const VideoEditingTools: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Creator Tips • August 2, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Best Video Editing Tools for Indian Creators
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Creating compelling content for IndeeClips requires more than just a good camera—you need the right editing tools to transform raw footage into polished, engaging videos. Whether you're a beginner working on a tight budget or a professional creator, this guide covers the best video editing software available to Indian creators in 2026.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          For Beginners: Free & Easy-to-Learn Tools
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          1. CapCut (Mobile & Desktop) - Best Free Option
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>Price:</strong> Free | <strong>Platforms:</strong> Android, iOS, Windows, Mac
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          CapCut has become the go-to editor for millions of Indian creators, and for good reason. It's completely free, surprisingly powerful, and available on both mobile and desktop platforms.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Perfect for:</strong> Clips, short-form content, quick edits, and beginners learning the ropes</li>
          <li><strong>Key features:</strong> Auto-captions in Hindi and English, trending effects, music library, keyframe animation, green screen support</li>
          <li><strong>Why Indian creators love it:</strong> Works smoothly even on budget smartphones, supports regional languages, and has templates optimized for vertical video</li>
          <li><strong>Learning curve:</strong> Very easy—you can create your first edited video within 30 minutes</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          2. DaVinci Resolve (Desktop) - Professional Power, Free Price
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>Price:</strong> Free (Studio version ₹22,500 one-time) | <strong>Platforms:</strong> Windows, Mac, Linux
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          DaVinci Resolve is a Hollywood-grade editor that's completely free for most creators. If you're serious about content creation and want professional results without the professional price tag, this is your tool.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Perfect for:</strong> Long-form content, cinematic videos, color grading, multi-track editing</li>
          <li><strong>Key features:</strong> Advanced color correction, professional audio mixing, motion graphics, collaboration tools</li>
          <li><strong>System requirements:</strong> Requires a decent PC/laptop (8GB RAM minimum, dedicated graphics recommended)</li>
          <li><strong>Learning curve:</strong> Moderate—plan for 2-3 weeks to feel comfortable with basics</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          3. Kinemaster (Mobile) - Mobile Powerhouse
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>Price:</strong> Free with watermark, ₹399/month for premium | <strong>Platforms:</strong> Android, iOS
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Kinemaster has been a favorite among Indian mobile creators for years. It brings desktop-level editing capabilities to your smartphone with an intuitive interface designed for touchscreens.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Perfect for:</strong> On-the-go editing, vlogs, tutorials, creators who prefer mobile workflows</li>
          <li><strong>Key features:</strong> Multi-layer editing, chroma key, speed control, transitions, built-in asset store</li>
          <li><strong>Worth noting:</strong> Free version adds watermark; premium subscription unlocks all features and removes branding</li>
          <li><strong>Learning curve:</strong> Easy—similar to CapCut but with more advanced features</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          For Intermediate Creators: Budget-Friendly Paid Options
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          4. Filmora (Desktop) - Best Value for Money
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>Price:</strong> ₹5,999/year or ₹13,999 lifetime | <strong>Platforms:</strong> Windows, Mac
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Filmora strikes the perfect balance between beginner-friendly interface and professional features. It's what many successful IndeeClips creators use when they outgrow free tools but aren't ready for the complexity of Premiere Pro.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Perfect for:</strong> Educational content, vlogs, gaming videos, business presentations</li>
          <li><strong>Key features:</strong> Extensive effect library, AI-powered tools, speech-to-text, green screen, motion tracking</li>
          <li><strong>Why creators choose it:</strong> Much easier than Premiere Pro, more powerful than basic editors, regular template updates</li>
          <li><strong>Learning curve:</strong> Easy to moderate—you'll be productive within days</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          5. HitFilm Pro (Desktop) - VFX on a Budget
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>Price:</strong> Free version available, Pro at ₹19,999/year | <strong>Platforms:</strong> Windows, Mac
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          If your content involves visual effects, motion graphics, or cinematic elements, HitFilm offers professional-grade VFX tools at a fraction of Adobe After Effects' cost.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Perfect for:</strong> Short films, sci-fi content, gaming montages, creators who want Hollywood-style effects</li>
          <li><strong>Key features:</strong> 3D compositing, particle effects, 900+ VFX presets, color grading, professional audio tools</li>
          <li><strong>Learning curve:</strong> Moderate to steep—requires investment in learning, especially for VFX</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          For Professional Creators: Industry Standard Tools
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          6. Adobe Premiere Pro (Desktop) - Industry Standard
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>Price:</strong> ₹1,675/month (Creative Cloud subscription) | <strong>Platforms:</strong> Windows, Mac
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Premiere Pro is what most professional content creators and film editors use worldwide. If you're building a career in content creation, learning Premiere Pro is an investment in your professional skills.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Perfect for:</strong> Professional creators, teams, multi-platform content, advanced workflows</li>
          <li><strong>Key features:</strong> Unlimited tracks, advanced color grading, seamless After Effects integration, collaborative workflows, plugin ecosystem</li>
          <li><strong>Why go pro:</strong> Industry-standard skills, maximum flexibility, integration with other Adobe tools</li>
          <li><strong>Learning curve:</strong> Steep—expect 1-2 months to become proficient</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          7. Final Cut Pro (Mac Only) - Apple's Professional Editor
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>Price:</strong> ₹29,900 one-time purchase | <strong>Platforms:</strong> Mac only
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          For Mac users, Final Cut Pro offers blazing-fast performance thanks to its optimization for Apple Silicon. The one-time purchase makes it more affordable than Adobe's subscription in the long run.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Perfect for:</strong> Mac-based creators, fast-turnaround content, multi-cam editing</li>
          <li><strong>Key features:</strong> Magnetic timeline, incredible rendering speed on M-series chips, 360° video editing, advanced color grading</li>
          <li><strong>Worth noting:</strong> Once you pay ₹29,900, it's yours forever—no subscription</li>
          <li><strong>Learning curve:</strong> Moderate—different workflow from Premiere, but very logical once you adapt</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Choosing the Right Tool for Your Needs
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The "best" editing software depends entirely on your specific situation. Here's how to decide:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>You're just starting out:</strong> CapCut (mobile) or DaVinci Resolve (desktop). Both are free and powerful enough to create professional-looking content.</li>
          <li><strong>You create primarily on mobile:</strong> CapCut for ease or Kinemaster for more advanced features. Both work great for clips and short-form content on IndeeClips.</li>
          <li><strong>You're ready to invest (₹5,000-15,000):</strong> Filmora offers the best value, combining ease-of-use with professional features.</li>
          <li><strong>You create VFX-heavy content:</strong> HitFilm gives you After Effects-level capabilities without the After Effects price tag.</li>
          <li><strong>You're building a career in content:</strong> Learn Premiere Pro or Final Cut Pro. The skills translate across industries and the tools can handle any project.</li>
          <li><strong>You have a Mac:</strong> Final Cut Pro's one-time purchase (₹29,900) costs less than two years of Premiere Pro subscription and runs faster on Apple hardware.</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Tips for Getting Started
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Whichever tool you choose, keep these principles in mind:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Start simple:</strong> Don't try to learn every feature at once. Master basic cuts, transitions, and audio adjustment first.</li>
          <li><strong>Use keyboard shortcuts:</strong> They'll 10x your editing speed once you learn them.</li>
          <li><strong>Watch tutorials:</strong> YouTube and official documentation are free learning resources. Invest 2-3 hours learning your chosen software.</li>
          <li><strong>Create templates:</strong> Save time by building reusable intro/outro templates, color grades, and effect presets.</li>
          <li><strong>Upgrade when you're limited, not bored:</strong> Only switch to paid software when your free tool can't do what you need, not just because you want something new.</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            The Tool Doesn't Make the Creator
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Remember: the best editing software in the world won't make boring content interesting. Focus on storytelling, pacing, and value first. The editing tool is just that—a tool. Many successful IndeeClips creators started with free mobile apps and still use them today because they've mastered their craft.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Start with what's available, learn the fundamentals, and upgrade when your skills—not your curiosity—demand it. Need more creator resources? Contact us at{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default VideoEditingTools;
